import React, { useState } from 'react';
import { formatApiDate } from '../utils/security';
import { formatMesafeKm } from '../utils/mesafeFormat';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import { fetchHakedisDetay, hakedisGuncelle, hakedisOnayla, hakedisIptal } from '../services/hakedisService';
import { fetchRandevuBedeliDetay, RANDEVU_BEDELI_SAYAC_YENILE_EVENT } from '../services/randevuBedeliService';
import RandevuBedeliDetayModal from './RandevuBedeliDetayModal';
import IntegerInput from './IntegerInput';
import { parseAdet } from '../utils/integerInput';
import './HakedisDetayModal.css';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '—';
  return `${parseFloat(amount).toFixed(2)} ₺`;
};

const DURUM_LABELS = {
  OnayBekliyor: 'Onay Bekliyor',
  FaturaBekleyen: 'Fatura Bekleyen',
  Tamamlandi: 'Tamamlandı',
  IptalEdildi: 'İptal Edildi',
};

const HakedisDetayModal = ({ hakedis, onClose, onRefresh }) => {
  const { user } = useAuth();
  const { confirm } = useConfirmDialog();
  const isAdmin = user?.rol === 'admin' || user?.rol === 'mod' || user?.rol === 'moderator';
  const [editMode, setEditMode] = useState(false);
  const [localData, setLocalData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRandevuBedeli, setSelectedRandevuBedeli] = useState(null);

  const data = localData || hakedis;
  if (!data) return null;

  const randevuBedelleri = data.randevuBedelleri || [];
  const hasRandevuBedelleri = Array.isArray(randevuBedelleri) && randevuBedelleri.length > 0;

  const randevu = data.randevu || data.randevuId;
  const randevuNumarasi = hasRandevuBedelleri
    ? `${randevuBedelleri.length} randevu`
    : (randevu?.randevuNumarasi || data.randevuNumarasi || '—');
  const teknikServis = data.teknikServis || data.teknikServisId;
  const teknikServisAdi = (typeof teknikServis === 'object' ? teknikServis?.ad : null) || data.teknikServisAdi || null;
  const mesafe = parseFloat(data.mesafe) || 0;
  const mesafeGidisDonus = data.mesafeGidisDonus === true;
  const yolBedeli = parseFloat(data.yolBedeli) || 0;
  const birimYolBedeli = mesafe > 0 ? yolBedeli / mesafe : 0;

  const canEdit = isAdmin && data.durum === 'OnayBekliyor';
  const canIptal = isAdmin && (data.durum === 'OnayBekliyor' || data.durum === 'FaturaBekleyen');

  const handleSave = async () => {
    if (!data.id && !data._id) return;
    const id = data.id || data._id;
    setSubmitting(true);
    try {
      const res = await hakedisGuncelle(id, {
        yapilanIslemler: data.yapilanIslemler || [],
        ilaveIslemler: data.ilaveIslemler || [],
        degistirilenParcalar: data.degistirilenParcalar || [],
        yolBedeli: data.yolBedeli,
      });
      if (res?.basarili) {
        const refreshed = await fetchHakedisDetay(id);
        if (refreshed?.basarili?.veri?.hakedis) {
          setLocalData(refreshed.veri.hakedis);
        }
        setEditMode(false);
        onRefresh?.();
        window.showNotification?.('success', 'Hakediş güncellendi');
      } else {
        window.showNotification?.('error', res?.mesaj || 'Güncellenemedi');
      }
    } catch (e) {
      window.showNotification?.('error', e?.message || 'Güncellenemedi');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOnayla = async () => {
    const id = data.id || data._id;
    if (!id) return;
    setSubmitting(true);
    try {
      const res = await hakedisOnayla(id);
      if (res?.basarili) {
        onClose?.();
        onRefresh?.();
        window.showNotification?.('success', 'Hakediş onaylandı');
      } else {
        window.showNotification?.('error', res?.mesaj || 'Onaylanamadı');
      }
    } catch (e) {
      window.showNotification?.('error', e?.message || 'Onaylanamadı');
    } finally {
      setSubmitting(false);
    }
  };

  const handleIptal = async () => {
    const id = data.id || data._id;
    if (!id) return;
    const isOnayBekliyor = data.durum === 'OnayBekliyor';
    const ok = await confirm({
      title: 'Hakedişi iptal et',
      message: isOnayBekliyor
        ? 'Hakediş talebini iptal etmek istediğinize emin misiniz? Randevu bedelleri hakedişten çıkarılır; cari hareket oluşturulmaz.'
        : 'Hakedişi iptal etmek istediğinize emin misiniz? İçindeki randevu bedelleri hakedişten çıkarılacak (tekrar hakedişe eklenebilir olacak), cari ters kayıt oluşturulacak ve hakediş iptal edilecek.',
      variant: 'danger',
      confirmLabel: 'Evet, iptal et',
    });
    if (!ok) return;
    setSubmitting(true);
    try {
      const res = await hakedisIptal(id);
      if (res?.basarili) {
        onClose?.();
        onRefresh?.();
        window.dispatchEvent(new Event(RANDEVU_BEDELI_SAYAC_YENILE_EVENT));
        window.showNotification?.('success', 'Hakediş iptal edildi');
      } else {
        window.showNotification?.('error', res?.mesaj || 'İptal başarısız');
      }
    } catch (e) {
      window.showNotification?.('error', e?.message || 'İptal başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  const updateItem = (type, index, field, value) => {
    const key = type === 'islem' ? 'yapilanIslemler' : type === 'ilave' ? 'ilaveIslemler' : 'degistirilenParcalar';
    const arr = [...(data[key] || [])];
    if (!arr[index]) return;
    arr[index] = { ...arr[index], [field]: value };
    const next = { ...data, [key]: arr };
    // Recalc toplamTutar
    let toplam = 0;
    (next.yapilanIslemler || []).forEach(i => { toplam += (parseFloat(i.fiyat) || 0) * (parseInt(i.adet) || 1); });
    (next.ilaveIslemler || []).forEach(i => { toplam += (parseFloat(i.fiyat) || 0) * (parseInt(i.adet) || 1); });
    (next.degistirilenParcalar || []).forEach(p => {
      const f = parseFloat(p.fiyat) || 0;
      if (f > 0) toplam += f * (parseInt(p.adet) || 1);
    });
    const yb = parseFloat(data.yolBedeli) || 0;
    next.toplamTutar = toplam + yb;
    setLocalData(next);
  };

  const removeItem = (type, index) => {
    const key = type === 'islem' ? 'yapilanIslemler' : type === 'ilave' ? 'ilaveIslemler' : 'degistirilenParcalar';
    const arr = [...(data[key] || [])];
    arr.splice(index, 1);
    const next = { ...data, [key]: arr };
    const yb = parseFloat(next.yolBedeli) || 0;
    let toplam = 0;
    (next.yapilanIslemler || []).forEach(i => { toplam += (parseFloat(i.fiyat) || 0) * (parseInt(i.adet) || 1); });
    (next.ilaveIslemler || []).forEach(i => { toplam += (parseFloat(i.fiyat) || 0) * (parseInt(i.adet) || 1); });
    (next.degistirilenParcalar || []).forEach(p => {
      const f = parseFloat(p.fiyat) || 0;
      if (f > 0) toplam += f * (parseInt(p.adet) || 1);
    });
    next.toplamTutar = toplam + yb;
    setLocalData(next);
  };

  const updateYolBedeli = (value) => {
    const yb = parseFloat(value);
    const newYol = isNaN(yb) || yb < 0 ? 0 : yb;
    const next = { ...data, yolBedeli: newYol };
    let toplam = 0;
    (next.yapilanIslemler || []).forEach(i => { toplam += (parseFloat(i.fiyat) || 0) * (parseInt(i.adet) || 1); });
    (next.ilaveIslemler || []).forEach(i => { toplam += (parseFloat(i.fiyat) || 0) * (parseInt(i.adet) || 1); });
    (next.degistirilenParcalar || []).forEach(p => {
      const f = parseFloat(p.fiyat) || 0;
      if (f > 0) toplam += f * (parseInt(p.adet) || 1);
    });
    next.toplamTutar = toplam + newYol;
    setLocalData(next);
  };

  const renderRow = (label, kod, adet, fiyat, type, idx, toplamOverride) => {
    const fiyatDisplay = fiyat != null && parseFloat(fiyat) >= 0 ? formatCurrency(fiyat) : '—';
    const toplamSatir = toplamOverride != null ? toplamOverride : (parseFloat(fiyat) || 0) * (parseInt(adet) || 1);
    const toplamDisplay = toplamSatir > 0 || toplamOverride != null ? formatCurrency(toplamSatir) : '—';
    const ad = kod ? `${label || '—'} (${kod})` : (label || '—');
    return (
      <tr key={idx}>
        <td>{ad}</td>
        <td style={{ textAlign: 'center' }}>
          {editMode && (type === 'islem' || type === 'ilave' || type === 'parca') ? (
            <IntegerInput
              min={1}
              value={adet || 1}
              onChange={(v) => updateItem(type, idx, 'adet', parseAdet(v, { min: 1 }))}
              style={{ width: 56, padding: '4px 6px', textAlign: 'center' }}
            />
          ) : (
            adet || 1
          )}
        </td>
        <td style={{ textAlign: 'right' }}>{fiyatDisplay}</td>
        <td style={{ textAlign: 'right' }}>{toplamDisplay}</td>
        {editMode && type && (
          <td style={{ width: 40 }}>
            <button
              type="button"
              className="category-action-btn delete-btn"
              onClick={() => removeItem(type, idx)}
              title="Sil"
            >
              <i className="fas fa-trash"></i>
            </button>
          </td>
        )}
      </tr>
    );
  };

  return (
    <div className="modal-overlay hakedis-detay-modal" onClick={onClose}>
      <div className="modal-content hakedis-invoice-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Hakediş Detayı {data.numara ? `— ${data.numara}` : `— Randevu ${randevuNumarasi}`}</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          <div className="hakedis-invoice-header">
            <div className="hakedis-invoice-meta">
              {data.numara && <span><strong>Hakediş No:</strong> {data.numara}</span>}
              <span><strong>{hasRandevuBedelleri ? 'Randevu sayısı:' : 'Randevu No:'}</strong> {randevuNumarasi}</span>
              <span><strong>Teknik Servis:</strong> {teknikServisAdi || '—'}</span>
              <span><strong>Oluşturma:</strong> {formatApiDate(data.olusturmaTarihi)}</span>
            </div>
            <span className={`status-badge status-${(data.durum || '').toLowerCase()}`}>
              {DURUM_LABELS[data.durum] || data.durum || '—'}
            </span>
          </div>

          {hasRandevuBedelleri && (
            <div className="hakedis-randevu-bedelleri">
              <h3 className="hakedis-randevu-bedelleri-title">İçerdiği randevular</h3>
              <div className="hakedis-randevu-bedelleri-list">
                <table className="admins-table">
                  <thead>
                    <tr>
                      <th>Randevu No</th>
                      <th style={{ textAlign: 'right' }}>Bedel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {randevuBedelleri.map((rb) => (
                      <tr
                        key={rb.id || rb.randevuId}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedRandevuBedeli(rb)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && setSelectedRandevuBedeli(rb)}
                      >
                        <td>{rb.randevuNumarasi || rb.randevu?.randevuNumarasi || '—'}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(rb.toplamTutar)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!hasRandevuBedelleri && (
          <table className="admins-table hakedis-invoice-table">
            <thead>
              <tr>
                <th>İşlem / Parça Adı / Kod</th>
                <th style={{ textAlign: 'center', width: 70 }}>Adet / Km</th>
                <th style={{ textAlign: 'right', width: 85 }}>Birim Fiyat</th>
                <th style={{ textAlign: 'right', width: 85 }}>Toplam</th>
                {editMode && canEdit && <th style={{ width: 40 }}></th>}
              </tr>
            </thead>
            <tbody>
              {(data.yapilanIslemler || []).map((i, idx) =>
                renderRow(i.islemAdi, i.islemKodu, i.adet, i.fiyat, 'islem', idx)
              )}
              {(data.ilaveIslemler || []).map((i, idx) =>
                renderRow(i.islemAdi, null, i.adet, i.fiyat, 'ilave', idx)
              )}
              {(data.degistirilenParcalar || []).map((p, idx) =>
                renderRow(p.parcaAdi, p.parcaKodu, p.adet, p.fiyat, 'parca', idx)
              )}
              {mesafe > 0 && (
                <tr key="yol">
                  <td>Yol Bedeli</td>
                  <td style={{ textAlign: 'center' }}>{formatMesafeKm(mesafe, mesafeGidisDonus)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(birimYolBedeli)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {editMode && canEdit ? (
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        value={yolBedeli || ''}
                        onChange={(e) => updateYolBedeli(e.target.value)}
                        style={{ width: 90, padding: '4px 6px', textAlign: 'right' }}
                      />
                    ) : (
                      formatCurrency(yolBedeli)
                    )}
                  </td>
                  {editMode && canEdit && <td></td>}
                </tr>
              )}
            </tbody>
          </table>
          )}

          <div className="hakedis-invoice-footer">
            <div className="hakedis-invoice-total">
              <span>TOPLAM TUTAR</span>
              <strong>{formatCurrency(data.toplamTutar)}</strong>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          {canEdit && !hasRandevuBedelleri && (
            <>
              {!editMode ? (
                <button type="button" className="secondary-btn" onClick={() => setEditMode(true)}>
                  <i className="fas fa-edit"></i> Düzenle
                </button>
              ) : (
                <button type="button" className="secondary-btn" onClick={handleSave} disabled={submitting}>
                  {submitting ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              )}
              <button
                type="button"
                className="primary-btn"
                onClick={handleOnayla}
                disabled={submitting || editMode}
              >
                {submitting ? 'Onaylanıyor...' : 'Onayla'}
              </button>
            </>
          )}
          {canIptal && (
            <button
              type="button"
              className="btn danger-btn"
              onClick={handleIptal}
              disabled={submitting}
              style={{ marginLeft: canEdit ? 8 : 0 }}
              title="Hakedişi tamamen iptal et"
            >
              <i className="fas fa-times-circle"></i> Hakedişi İptal Et
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={onClose}>Kapat</button>
        </div>
      </div>

      {selectedRandevuBedeli && (
        <RandevuBedeliDetayModal
          bedel={selectedRandevuBedeli}
          onClose={() => setSelectedRandevuBedeli(null)}
          fetchDetay={fetchRandevuBedeliDetay}
        />
      )}
    </div>
  );
};

export default HakedisDetayModal;
